import React from 'react';
import styles from './Transition.module.css';
import { COLUMN_DEFS, MIGRATION_TYPES, STATUS_OPTIONS, resetColumns } from './constants';

export function Modal({ title, onClose, children, footer, width }) {
    return (
        <div className={styles.scrim} onMouseDown={onClose}>
            <div
                className={styles.dialog}
                style={width ? { width } : undefined}
                role="dialog"
                aria-modal="true"
                aria-label={title}
                onMouseDown={event => event.stopPropagation()}>
                <div className={styles.dialogHead}>
                    <h2 className={styles.dialogTitle}>{title}</h2>
                    <button type="button" className={styles.closeBtn} onClick={onClose} aria-label={`Close ${title}`}>
                        ✕
                    </button>
                </div>
                <div className={styles.dialogBody}>{children}</div>
                {footer ? <div className={styles.dialogFoot}>{footer}</div> : null}
            </div>
        </div>
    );
}

export function HowImportingDialog({ onClose, onStart }) {
    return (
        <Modal
            title="How importing works"
            onClose={onClose}
            footer={
                <button type="button" className={styles.primaryBtn} onClick={onStart}>
                    Start import
                </button>
            }>
            Choose Excel or CSV files, review worksheets and field matches, then confirm changes. Original names and values are preserved, and nothing changes before confirmation.
        </Modal>
    );
}

export function CompletedDialog({ result, history, onClose, onOverview, onTracker }) {
    return (
        <Modal
            title={result ? 'Import completed' : 'Import history'}
            onClose={onClose}
            footer={
                <>
                    <button type="button" className={styles.primaryBtn} onClick={onOverview}>
                        Open Transition Overview
                    </button>
                    <button type="button" className={styles.ghostBtn} onClick={onTracker}>
                        View Tracker
                    </button>
                </>
            }>
            {result ? <p>{importResultText(result)}</p> : null}
            {(history || []).length === 0 && !result ? <p>No spreadsheet imports for this project yet.</p> : null}
            {(history || []).map(batch => (
                <p key={batch.id}>{historyLine(batch)}</p>
            ))}
        </Modal>
    );
}

const HISTORY_STATUS = {
    COMMITTED: 'Imported',
    CANCELLED: 'Cancelled',
    READY_FOR_COMMIT: 'Ready to import',
    DRAFT: 'Draft',
    FAILED: 'Failed',
    UPLOADING: 'Uploading',
    PROCESSING: 'Reading file',
    COMMITTING: 'Import in progress (background)',
};

function historyLine(batch) {
    const file = (batch.filenames || []).filter(Boolean).join(', ') || 'No file attached';
    const status = HISTORY_STATUS[batch.status] || batch.status;
    const when = batch.created_at ? new Date(batch.created_at).toLocaleString() : '';
    const created = batch.summary?.created ? `${batch.summary.created} created` : '';
    return [file, status, when, created].filter(Boolean).join(' · ');
}

function importResultText(result) {
    const counts = result.result || result;
    return `${counts.created || 0} created, ${counts.updated || 0} updated, ${counts.held_for_review || 0} held for review, ${counts.missing_from_latest || 0} missing from the latest file.`;
}

export function VersionConflictDialog({ title, onClose, onReload }) {
    return (
        <Modal
            title="Updated by another user"
            onClose={onClose}
            footer={
                <>
                    <button type="button" className={styles.ghostBtn} onClick={onClose}>
                        Cancel
                    </button>
                    <button type="button" className={styles.primaryBtn} onClick={onReload}>
                        Reload latest
                    </button>
                </>
            }>
            <p style={{ marginTop: 0 }}>
                {title ? `“${title}” was changed elsewhere.` : 'This row was changed elsewhere.'} Reload the latest version before editing again.
            </p>
        </Modal>
    );
}

export function ConflictDialog({ row, onClose, onChoice }) {
    const fields = row?.fields || [];
    return (
        <Modal
            title="Review incoming change"
            onClose={onClose}
            footer={
                <button type="button" className={styles.mutedBtn} onClick={onClose}>
                    Done
                </button>
            }>
            <p style={{ marginTop: 0 }}>{row?.title || 'This row'} has values that differ from the current Lens record. Keep current is the default.</p>
            <table className={styles.table} style={{ minWidth: 0 }}>
                <thead>
                    <tr>
                        <th>Field</th>
                        <th>Current in Lens</th>
                        <th>Incoming</th>
                        <th>Choice</th>
                    </tr>
                </thead>
                <tbody>
                    {fields.length === 0 ? (
                        <tr>
                            <td colSpan={4}>No field-level conflict is waiting on this row.</td>
                        </tr>
                    ) : null}
                    {fields.map(field => (
                        <tr key={field.id}>
                            <td>{field.field_key}</td>
                            <td>{displayValue(field.current)}</td>
                            <td>{displayValue(field.incoming)}</td>
                            <td>
                                <button type="button" className={styles.textBtn} onClick={() => onChoice(field.id, 'KEEP_LENS')}>
                                    Keep current
                                </button>
                                <button type="button" className={styles.textBtn} onClick={() => onChoice(field.id, 'USE_IMPORTED')}>
                                    Use imported
                                </button>
                            </td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </Modal>
    );
}

function displayValue(value) {
    if (value === null || value === undefined || value === '') return '—';
    if (typeof value === 'object') return JSON.stringify(value);
    return String(value);
}

export function FailedDialog({ message, onClose, onRetry, onReview, onBack }) {
    return (
        <Modal
            title="Import could not be completed"
            onClose={onClose}
            footer={
                <>
                    <button type="button" className={styles.primaryBtn} onClick={onRetry}>
                        Retry
                    </button>
                    <button type="button" className={styles.ghostBtn} onClick={onReview}>
                        Review file
                    </button>
                    <button type="button" className={styles.mutedBtn} onClick={onBack}>
                        Back to Transition
                    </button>
                </>
            }>
            {message || 'The file could not be imported. No records were changed.'}
        </Modal>
    );
}

export function AddRowDialog({ form, onChange, onClose, onSubmit }) {
    const canAdd = form.title.trim().length > 0;
    return (
        <Modal
            title="Add row"
            onClose={onClose}
            footer={
                <>
                    <button type="button" className={styles.ghostBtn} onClick={onClose}>
                        Cancel
                    </button>
                    <button type="button" className={styles.primaryBtn} disabled={!canAdd} onClick={onSubmit}>
                        Add row
                    </button>
                </>
            }>
            <label className={styles.field}>
                <span className={styles.fieldLabel}>
                    Item / Description <span className={styles.required}>*</span>
                </span>
                <input className={styles.input} aria-label="Item / Description" value={form.title} onChange={event => onChange({ title: event.target.value })} />
            </label>
            <div className={styles.formGrid}>
                <label className={styles.field}>
                    <span className={styles.fieldLabel}>Business Function</span>
                    <input className={styles.input} aria-label="Business Function" value={form.businessFunction} onChange={event => onChange({ businessFunction: event.target.value })} />
                </label>
                <label className={styles.field}>
                    <span className={styles.fieldLabel}>Owner</span>
                    <input className={styles.input} aria-label="Owner" value={form.owner} onChange={event => onChange({ owner: event.target.value })} />
                </label>
                <SelectField label="Status" value={form.status} options={STATUS_OPTIONS.map(option => option.value)} labels={STATUS_OPTIONS} onChange={value => onChange({ status: value })} />
                <SelectField label="Migration Type" value={form.migrationType} options={MIGRATION_TYPES} onChange={value => onChange({ migrationType: value })} />
                <label className={styles.field}>
                    <span className={styles.fieldLabel}>Target Date</span>
                    <input className={styles.input} type="date" value={form.targetDate} onChange={event => onChange({ targetDate: event.target.value })} />
                </label>
            </div>
        </Modal>
    );
}

function SelectField({ label, value, options, labels, onChange }) {
    return (
        <label className={styles.field}>
            <span className={styles.fieldLabel}>{label}</span>
            <select className={styles.select} aria-label={label} value={value} onChange={event => onChange(event.target.value)}>
                {options.map(option => (
                    <option key={option} value={option}>
                        {labels ? labels.find(item => item.value === option)?.label || option : option}
                    </option>
                ))}
            </select>
        </label>
    );
}

export function ColumnsDialog({ draft, onToggle, onClose, onApply, onReset }) {
    return (
        <Modal
            title="Columns"
            onClose={onClose}
            footer={
                <>
                    <button type="button" className={styles.primaryBtn} onClick={onApply}>
                        Apply columns
                    </button>
                    <button type="button" className={styles.ghostBtn} onClick={onReset}>
                        Reset
                    </button>
                </>
            }>
            <div className={styles.checkList}>
                {COLUMN_DEFS.map(column => (
                    <label key={column.id} className={styles.checkLine}>
                        <input className={styles.check} type="checkbox" checked={Boolean(draft[column.id])} onChange={() => onToggle(column.id)} />
                        {column.label}
                    </label>
                ))}
            </div>
        </Modal>
    );
}

export { resetColumns };

export function PasteDialog({
    step,
    text,
    headers,
    preview,
    duplicates,
    error,
    busy,
    workstreams,
    workstreamId,
    onWorkstream,
    onText,
    onHeaders,
    onClose,
    onPreview,
    onBack,
    onReview,
    onConfirm,
}) {
    const needsWorkstream = (workstreams || []).length > 0;
    const workstreamReady = !needsWorkstream || workstreamId;

    if (step === 'preview') {
        return (
            <Modal
                title="Paste rows"
                onClose={onClose}
                footer={
                    <>
                        <button type="button" className={styles.ghostBtn} onClick={onBack}>
                            Back
                        </button>
                        <button type="button" className={styles.ghostBtn} onClick={onClose}>
                            Cancel
                        </button>
                        <button type="button" className={styles.primaryBtn} onClick={onReview} disabled={!workstreamReady}>
                            Review changes
                        </button>
                    </>
                }>
                <label className={styles.field}>
                    <span className={styles.fieldLabel}>Destination business area / Workstream</span>
                    {needsWorkstream ? (
                        <select className={styles.select} aria-label="Destination Workstream" value={workstreamId || ''} onChange={event => onWorkstream(event.target.value)}>
                            <option value="">Choose a workstream</option>
                            {workstreams.map(option => (
                                <option key={option.id} value={String(option.id)}>
                                    {option.name}
                                </option>
                            ))}
                        </select>
                    ) : (
                        <p className={styles.finePrint}>No workstream assignment required for your access.</p>
                    )}
                </label>
                <p className={styles.sheetMeta}>{(preview?.columns || []).map(column => `${column.header} → ${column.suggestion?.proposed_field || 'custom'}`).join(' · ')}</p>
                {error ? <p className={styles.warn}>{error}</p> : null}
                <table className={styles.table} style={{ minWidth: 0, marginTop: 12 }}>
                    <thead>
                        <tr>
                            {(preview?.columns || []).map(column => (
                                <th key={column.index}>{column.header}</th>
                            ))}
                            <th>Check</th>
                        </tr>
                    </thead>
                    <tbody>
                        {(preview?.rows || []).map((row, index) => (
                            <tr key={`${row.title}-${index}`}>
                                {(row.values || []).map((value, valueIndex) => (
                                    <td key={valueIndex}>{value}</td>
                                ))}
                                <td className={row.check === 'ready' ? styles.matched : styles.review}>{row.check === 'ready' ? 'Ready' : 'Needs a title'}</td>
                            </tr>
                        ))}
                    </tbody>
                </table>
                {duplicates?.length ? <p className={styles.warn}>Already in the tracker: {duplicates.join(', ')}. Confirming adds another copy.</p> : null}
                <p className={styles.finePrint}>Nothing is saved until you confirm. Status values are not inferred from the pasted text.</p>
            </Modal>
        );
    }

    if (step === 'confirm') {
        return (
            <Modal
                title="Paste rows"
                onClose={onClose}
                footer={
                    <>
                        <button type="button" className={styles.ghostBtn} onClick={onBack}>
                            Back
                        </button>
                        <button type="button" className={styles.ghostBtn} onClick={onClose}>
                            Cancel
                        </button>
                        <button type="button" className={styles.primaryBtn} onClick={onConfirm} disabled={busy || readyCount(preview) === 0}>
                            Confirm {readyCount(preview)} records
                        </button>
                    </>
                }>
                <p style={{ marginTop: 0 }}>
                    <strong>Nothing has been applied yet.</strong>
                </p>
                <p>Review the summary, then confirm which changes Lens should add.</p>
                <div className={styles.countGrid} style={{ gridTemplateColumns: 'repeat(3, 1fr)' }}>
                    <Count label="Ready" value={readyCount(preview)} />
                    <Count label="Needs a title" value={(preview?.rows || []).filter(row => row.check !== 'ready').length} />
                    <Count label="Updates" value="0" />
                </div>
                {duplicates?.length ? <p className={styles.warn}>Already in the tracker: {duplicates.join(', ')}. Confirming adds another copy.</p> : null}
                {error ? <p className={styles.warn}>{error}</p> : null}
                <p className={styles.finePrint}>Rows are added with clipboard source metadata. Unrecognised statuses stay unknown.</p>
            </Modal>
        );
    }

    return (
        <Modal
            title="Paste rows"
            onClose={onClose}
            footer={
                <>
                    <button type="button" className={styles.ghostBtn} onClick={onClose}>
                        Cancel
                    </button>
                    <button type="button" className={styles.primaryBtn} onClick={onPreview} disabled={!text.trim()}>
                        Preview
                    </button>
                </>
            }>
            <p style={{ marginTop: 0 }}>Paste tab-separated rows copied from Excel or Google Sheets.</p>
            <textarea className={styles.textarea} value={text} onChange={event => onText(event.target.value)} aria-label="Pasted rows" />
            <label className={styles.checkLine}>
                <input className={styles.check} type="checkbox" checked={headers} onChange={event => onHeaders(event.target.checked)} />
                First row contains column headers
            </label>
        </Modal>
    );
}

function readyCount(preview) {
    return (preview?.rows || []).filter(row => row.check === 'ready').length;
}

function Count({ label, value }) {
    return (
        <div className={styles.countCard}>
            <p className={styles.countLabel}>{label}</p>
            <p className={styles.countValue}>{value}</p>
        </div>
    );
}

export function SheetPreviewDialog({ sheet, loading, error, onClose }) {
    const headers = sheet?.preview?.headers || [];
    const rows = sheet?.preview?.rows || [];
    return (
        <Modal
            title={sheet?.name ? `Preview · ${sheet.name}` : 'Worksheet preview'}
            onClose={onClose}
            width={720}
            footer={
                <button type="button" className={styles.primaryBtn} onClick={onClose}>
                    Close
                </button>
            }>
            {loading ? <p className={styles.finePrint}>Loading preview…</p> : null}
            {error ? <p className={styles.warn}>{error}</p> : null}
            {!loading && !error && rows.length === 0 ? <p className={styles.sectionCopy}>No data rows are available for this worksheet yet.</p> : null}
            {!loading && !error && rows.length > 0 ? (
                <div className={styles.tableWrap}>
                    <table className={styles.table}>
                        <thead>
                            <tr>
                                {headers.map(header => (
                                    <th key={header}>{header || 'Column'}</th>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            {rows.map((row, rowIndex) => (
                                <tr key={rowIndex}>
                                    {headers.map((_, columnIndex) => (
                                        <td key={columnIndex}>{row[columnIndex] || '—'}</td>
                                    ))}
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            ) : null}
            {!loading && !error ? <p className={styles.finePrint}>Showing up to 15 rows from the parsed worksheet. Original file content is unchanged.</p> : null}
        </Modal>
    );
}

export function ChooseHeaderRowDialog({ sheet, loading, error, busy, onClose, onApply }) {
    const [choice, setChoice] = React.useState(null);
    React.useEffect(() => {
        if (sheet?.header_row_index !== undefined && sheet?.header_row_index !== null) {
            setChoice(sheet.header_row_index);
        }
    }, [sheet?.header_row_index, sheet?.id]);
    const grid = sheet?.header_grid || [];
    return (
        <Modal
            title={sheet?.name ? `Choose header row · ${sheet.name}` : 'Choose header row'}
            onClose={onClose}
            width={720}
            footer={
                <>
                    <button type="button" className={styles.ghostBtn} onClick={onClose} disabled={busy}>
                        Cancel
                    </button>
                    <button type="button" className={styles.primaryBtn} onClick={() => onApply(choice)} disabled={busy || choice === null || choice === undefined}>
                        Use this header row
                    </button>
                </>
            }>
            {loading ? <p className={styles.finePrint}>Reading worksheet…</p> : null}
            {error ? <p className={styles.warn}>{error}</p> : null}
            {!loading && !error ? (
                <>
                    <p className={styles.sectionCopy}>Pick the row that contains your column titles. Lens will re-read the sheet using that row as the header.</p>
                    <div className={styles.headerPickList}>
                        {grid.map(row => {
                            const label = row.cells.filter(Boolean).slice(0, 6).join(' · ') || `Row ${row.index + 1}`;
                            const selected = choice === row.index;
                            return (
                                <label key={row.index} className={selected ? styles.headerPickOn : styles.headerPick}>
                                    <input type="radio" name="header-row" checked={selected} onChange={() => setChoice(row.index)} />
                                    <span className={styles.headerPickLabel}>
                                        Row {row.index + 1}: {label}
                                    </span>
                                </label>
                            );
                        })}
                    </div>
                </>
            ) : null}
        </Modal>
    );
}
